import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import {
  greeniaPrivacyPolicy,
  modifyRequestData,
} from '../../services/axios-client/axeCommons';
import {
  publicEmploymentGetCustomerData,
  publicEmploymentGetInitialData,
  publicEmploymentSubmitAnswers,
  resetPublicEmploymentForm,
} from '../../features/forms/formsActions';
import generateFiscalCode from '../../common/helpers/generateFiscalCode';
// import { CenteredDivTitle } from '../../common/components/CenteredPageTitle.styles';
// import CenteredPageTitle from '../../common/components/CenteredPageTitle';
import { FormInputText } from '../../common/formInputsV2/FormInputText';
import { flagResponse, genders, numberOfOrganizations } from '../../common/constants';
import { FormInputRadio } from '../../common/formInputsV2/FormInputRadio';
import { FormInputSelect } from '../../common/formInputsV2/FormInputSelect';
import { FormInputDate } from '../../common/formInputsV2/FormInputDate';
import FormReactSelectInputBirthPlace from '../../common/formInputsV2/FormReactSelectInputBirthPlace';
import { FormInputAddressComplete } from '../../common/formInputsV2/FormInputAddressComplete';
import InputPrivacy from '../../common/componentsV2/inputs/InputPrivacy';
import { FormInputAddressCompleteManual } from '../../common/formInputsV2/FormInputAddressCompleteManual';
import getMinAllowedBirthday from '../../common/helpers/getMinAllowedBirthday';
import { PubblicoImpiegoFormFlowStyles, PubblicoImpiegoFormStyles } from './styles/PubblicoImpiegoFormFlow.styles';
import { answersPubblicoImpiegoFieldsBySteps, pubblicoImpiegoSchema } from './PubblicoImpiegoFormFlow.validations';
import { getBlockingFields, buildMissingFieldsMessage } from './PubblicoImpiegoFormFlow.stepErrors';
import ProgressBar from '../../common/componentsV2/ProgressBar';
import OrganizationData from './components/OrganizationData';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import { V2FontImport } from '../../common/formInputsV2/V2Fonts';
import { FormLayout } from '../../common/formInputsV2/FormLayout.styles';
import { SurfaceCardHeading } from '../../common/formInputsV2/Card.styles';
import { FooterNav } from '../../common/formInputsV2/FooterNav.styles';
import { PrimaryButton, SecondaryButton } from '../../common/formInputsV2/Button.styles';
import { sonnerErrorAlert } from '../../common/utils/sonnerAlerts';

function PubblicoImpiegoFormFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const skipCheckGenderRef = useRef(null);
  const state = useSelector((store) => store.forms.public_employment);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [showManualAdress, setShowManualAdress] = useState(false);
  const {
    loading,
    states,
    municipalities,
    dataPrivacy,
  } = state;
  const {
    handleSubmit,
    register,
    formState: { errors, touchedFields },
    setValue,
    trigger,
    getValues,
    watch,
    control,
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(pubblicoImpiegoSchema),
    shouldFocusError: false,
    defaultValues: {
      product: 'public_employment',
      source: 'greenia.it',
      policy_effective_date: '',
      privacies: [],
      name: '',
      surname: '',
      email: '',
      fiscal_code: '',
      address: '',
      house_number: '',
      postal_code: '',
      phone: '',
      gender: '',
      date_of_birth: '',
      country_of_birth_code: 'Z000',
      province_of_birth_code: null,
      commune_of_birth_code: null,
      born_abroad: false,
      residence_commune_code: '',
      residence_province_code: '',
      active_customer_id: null,
      existing_customer: false,
      number_of_organizations: '',
      organizations_data: [],
      retroactivity: '',
    },
  });

  const {
    fields, append, remove,
  } = useFieldArray({
    control,
    name: 'organizations_data',
  });

  const formDataFlow = watch();

  const updateFormData = (data) => {
    const keys = Object.keys(data);
    keys.forEach((key) => {
      setValue(key, data[key], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    });
    trigger(keys);
  };

  const isValid = (name) => {
    if (errors[name]?.message !== undefined) {
      return false;
    }

    if (touchedFields[name] === undefined) {
      return false;
    }

    return true;
  };

  const isValidOrganizationData = (index) => (field) => {
    if (errors?.organizations_data?.[index]?.[field]?.message) {
      return false;
    }

    if (!touchedFields?.organizations_data || !touchedFields.organizations_data[index]) {
      return false;
    }

    if (!touchedFields.organizations_data[index][field]) {
      return false;
    }

    return true;
  };

  const privacyItemChangeHandler = (value, item) => {
    if (!value.target.checked) {
      const newArray = [...formDataFlow.privacies];
      const index = newArray.indexOf(item.id);
      newArray.splice(index, 1);
      updateFormData({
        privacies: newArray,
      });
      return;
    }
    const answersNewArray = [...formDataFlow.privacies];
    answersNewArray.push(item.id);
    updateFormData({
      privacies: answersNewArray,
    });
  };

  const getCustomerData = async (customerId) => {
    publicEmploymentGetCustomerData(customerId);
  };

  useEffect(() => {
    publicEmploymentGetInitialData();

    return () => {
      resetPublicEmploymentForm();
    };
  }, []);

  useEffect(() => {
    if (
      location.state !== null
      && location.state.activeCustomerId !== undefined
    ) {
      getCustomerData(location.state.activeCustomerId);
    }
  }, []);

  const getFiscalCode = (birthDate) => {
    const d = birthDate.split('-');
    const fData = {
      name: formDataFlow.name,
      surname: formDataFlow.surname,
      gender: formDataFlow.gender,
      day: d[2],
      month: d[1],
      year: d[0],
    };
    if (formDataFlow.born_abroad) {
      fData.birthplace = states.filter(
        (s) => s.state_code === formDataFlow.country_of_birth_code,
      )[0].name;
      fData.birthplaceProvincia = 'EE';
    } else {
      fData.birthplace = municipalities.filter(
        (municipality) => municipality.cadastral_code === formDataFlow.commune_of_birth_code,
      )[0].name;
      fData.birthplaceProvincia = formDataFlow.province_of_birth_code;
    }
    return generateFiscalCode(fData);
  };

  const [showPrivacyErrors, setShowPrivacyErrors] = useState(false);

  const validateDataPrivacy = () => {
    let { error, message } = '';

    dataPrivacy.forEach((privacy) => {
      if (privacy.required === 1) {
        const userDataArray = { ...formDataFlow }; // make a separate copy of the object
        const index = userDataArray.privacies.indexOf(privacy.id);

        if (index < 0) {
          error = true;
          message = 'Tutti i campi sono obbligatori! Tutta la privacy con la stella deve essere controllata';
        }
      }
    });
    return { error, message };
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector('.is-invalid, [aria-invalid="true"], .general-input-error, .invalid-feedback.d-block');
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  const submitForm = async (formDataFlow) => {
    // Validate first — getFiscalCode() below throws on an empty
    // date_of_birth/commune_of_birth_code (looks up a municipality that
    // doesn't exist), which would otherwise blow up silently as an unhandled
    // rejection before the broker ever sees a validation error.
    const stepFields = answersPubblicoImpiegoFieldsBySteps[currentIndex - 1];
    const validateQuestions = await trigger(stepFields);
    if (validateQuestions !== true) {
      const blocking = getBlockingFields(errors, stepFields);
      sonnerErrorAlert(buildMissingFieldsMessage(blocking));
      scrollToFirstError();
      return;
    }

    const _userData = { ...formDataFlow };

    if (_userData.born_abroad && _userData.country_of_birth_code === 'Z000') {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Non puoi selezionare Italia come stato estero!');
      return;
    }

    if (
      _userData.born_abroad
      && (_userData.country_of_birth_code === null
        || _userData.country_of_birth_code === ''
        || _userData.country_of_birth_code == undefined)
    ) {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Devi selezionare uno stato estero!');
      return;
    }

    const validation = validateDataPrivacy();
    if (validation.error) {
      setShowPrivacyErrors(true);
      sonnerErrorAlert(validation.message);
      scrollToFirstError();
      return;
    }

    if (
      formDataFlow.date_of_birth !== ''
      && formDataFlow.date_of_birth !== null
    ) {
      _userData.date_of_birth = moment(formDataFlow.date_of_birth).format(
        'YYYY-MM-DD',
      );
    } else {
      _userData.date_of_birth = null;
    }
    _userData.policy_effective_date = moment(
      formDataFlow.policy_effective_date,
    ).format('YYYY-MM-DD');
    _userData.fiscal_code = formDataFlow.gender !== 'G'
      ? getFiscalCode(formDataFlow.date_of_birth)
      : null;
    delete _userData.existing_customer;

    publicEmploymentSubmitAnswers(_userData, navigate);
  };

  const nextButton = async () => {
    const stepFields = answersPubblicoImpiegoFieldsBySteps[currentIndex - 1];
    const res = await trigger(stepFields);

    if (res !== true) {
      const blocking = getBlockingFields(errors, stepFields);
      sonnerErrorAlert(buildMissingFieldsMessage(blocking));
      scrollToFirstError();
      return;
    }

    if (currentIndex === 2) {
      if (formDataFlow.born_abroad && formDataFlow.country_of_birth_code === 'Z000') {
        window.scrollTo(0, 0);
        sonnerErrorAlert('Non puoi selezionare Italia come stato estero!');
        return;
      }

      if (
        formDataFlow.born_abroad
        && (formDataFlow.country_of_birth_code === null
          || formDataFlow.country_of_birth_code === ''
          || formDataFlow.country_of_birth_code == undefined)
      ) {
        window.scrollTo(0, 0);
        sonnerErrorAlert('Devi selezionare uno stato estero!');
        return;
      }

      const validation = validateDataPrivacy();
      if (validation.error) {
        setShowPrivacyErrors(true);
        sonnerErrorAlert(validation.message);
        scrollToFirstError();
        return;
      }
      submitForm();
      return;
    }

    setCurrentIndex(currentIndex + 1);
    setTimeout(() => window.scrollTo(0, 0), 500);
  };

  useEffect(() => {
    if (skipCheckGenderRef.current === true) {
      skipCheckGenderRef.current = false;
      return;
    }

    const triggerValidation = {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    };

    if (
      ['M', 'F'].includes(formDataFlow.gender)
      && formDataFlow.date_of_birth === null
    ) {
      setValue('date_of_birth', '', triggerValidation);
      setValue('country_of_birth_code', 'Z000', triggerValidation);
      setValue('province_of_birth_code', null, triggerValidation);
      setValue('commune_of_birth_code', null, triggerValidation);
      setValue('born_abroad', false, triggerValidation);
      setValue('name', '', triggerValidation);
      setValue('surname', '', triggerValidation);
    }
  }, [formDataFlow.gender, formDataFlow.date_of_birth]);

  // On entering a step, mark that step's prefilled fields touched and validate
  // only them — so defaults show green borders without firing errors for
  // fields the user has not reached yet. Array fields (organizations_data) are
  // skipped: the field array manages its own touched state.
  useEffect(() => {
    const values = getValues();
    if (values === undefined) {
      return;
    }

    const stepFields = answersPubblicoImpiegoFieldsBySteps[currentIndex - 1] || [];
    const filledKeys = stepFields.filter(
      (key) => values[key] !== ''
        && values[key] !== null
        && values[key] !== undefined
        && !Array.isArray(values[key]),
    );
    if (filledKeys.length === 0) {
      return;
    }
    filledKeys.forEach((key) => {
      setValue(key, values[key], { shouldTouch: true, shouldDirty: true });
    });
    trigger(filledKeys);
  }, [currentIndex]);

  useEffect(() => {
    const callAsync = async () => {
      if (
        location.state !== null
        && location.state.requestToken !== undefined
      ) {
        skipCheckGenderRef.current = true;
        const {
          data: {
            data: { userData },
          },
        } = await modifyRequestData(location.state.requestToken);
        updateFormData(userData);
      }
    };

    callAsync();
  }, [location]);

  useEffect(() => {
    const number = parseInt(formDataFlow.number_of_organizations, 10);

    if (fields.length < number) {
      for (let i = fields.length; i < number; i++) {
        append({
          organization_name: '', type_of_organization: '', residence_commune_code: '', residence_province_code: '', insured_position: [], commune_population: '',
        });
      }
    } else if (fields.length > number) {
      for (let i = fields.length; i > number; i--) {
        remove(i - 1);
      }
    }
  }, [formDataFlow.number_of_organizations]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return <PageLoading />;
  }

  const stepsQuote = [
    'Dati del Impiego',
    'Dati Personali',
  ];

  return (
    <PubblicoImpiegoFormFlowStyles>
      <V2FontImport />
      <div className="d-flex align-content-center justify-content-center">
        <ProgressBar activeStep={currentIndex} steps={stepsQuote} />
      </div>
      {currentIndex === 1 && (
        <PubblicoImpiegoFormStyles>
          {/* Title removed — the v2 ProgressBar already shows the step name
            (steps list on desktop/tablet, "Passo X di N · label" on mobile).
          <CenteredDivTitle className="d-flex align-items-center justify-content-center gap-3">
            <CenteredPageTitle title="Completa i dati" />
          </CenteredDivTitle> */}
          <FormLayout className="form-container">
            <FormInputSelect
              label="Numero di enti o società per cui lavori"
              registration={register('number_of_organizations')}
              error={errors.number_of_organizations?.message}
              valid={isValid('number_of_organizations')}
              placeholder="-Seleziona-"
              options={numberOfOrganizations}
            />
            {formDataFlow.number_of_organizations && (
              <>
                {fields.map((field, index) => (
                  <OrganizationData
                    remove={remove}
                    index={index}
                    key={field.id}
                    values={formDataFlow.organizations_data[index]}
                    organization_name={`organizations_data.${index}.organization_name`}
                    residence_commune_code_name={`organizations_data.${index}.residence_commune_code`}
                    residence_province_code_name={`organizations_data.${index}.residence_province_code`}
                    error_organization_name={errors.organizations_data?.[index]?.organization_name?.message}
                    error_residence_province_code={errors.organizations_data?.[index]?.residence_province_code?.message}
                    type_of_organization={`organizations_data.${index}.type_of_organization`}
                    insured_position={`organizations_data.${index}.insured_position`}
                    error_type_of_organization={errors.organizations_data?.[index]?.type_of_organization?.message}
                    error_insured_position={errors.organizations_data?.[index]?.insured_position?.message}
                    commune_population={`organizations_data.${index}.commune_population`}
                    error_commune_population={errors.organizations_data?.[index]?.commune_population?.message}
                    isValid={isValidOrganizationData(index)}
                    register={register}
                    municipalities={municipalities}
                    updateFormData={updateFormData}
                  />
                ))}
              </>

            )}

            <FormInputRadio
              options={flagResponse}
              label="Retroattività Illimitata?"
              name="retroactivity"
              registration={register('retroactivity')}
              error={errors.retroactivity?.message}
            />
          </FormLayout>

        </PubblicoImpiegoFormStyles>

      )}

      {currentIndex === 2 && (
        <PubblicoImpiegoFormStyles
          onSubmit={handleSubmit(submitForm)}
        >
          {/* Title removed — the v2 ProgressBar already shows the step name
          (steps list on desktop/tablet, "Passo X di N · label" on mobile).
        <CenteredDivTitle className="d-flex align-items-center justify-content-center gap-3">
          <CenteredPageTitle title="Completa i dati" />
        </CenteredDivTitle> */}
          <div className="salute-tutorial">

            <FormLayout className="form-container">
              <FormInputRadio
                options={genders.filter((g) => g.value !== 'G')}
                label="Il contraente è"
                name="gender"
                registration={register('gender')}
                error={errors.gender?.message}
              />

              <div>
                <div>
                  <FormInputText
                    placeholder="Nome"
                    label="Nome"
                    registration={register('name')}
                    error={errors.name?.message}
                    valid={isValid('name')}
                  />
                  <FormInputText
                    placeholder="Cognome"
                    label="Cognome"
                    registration={register('surname')}
                    error={errors.surname?.message}
                    valid={isValid('surname')}
                  />
                  <FormInputDate
                    label="Data di nascita"
                    registration={register('date_of_birth')}
                    minDate={getMinAllowedBirthday()}
                    maxDate="2021-01-01"
                    error={errors.date_of_birth?.message}
                    valid={isValid('date_of_birth')}
                  />
                  <FormReactSelectInputBirthPlace
                    states={states}
                    options={municipalities}
                    label="Luogo di nascita"
                    name="commune_of_birth_code"
                    values={{
                      commune_of_birth_code: formDataFlow.commune_of_birth_code,
                      province_of_birth_code:
                        formDataFlow.province_of_birth_code,
                      born_abroad: formDataFlow.born_abroad,
                      country_of_birth_code: formDataFlow.country_of_birth_code,
                    }}
                    onChange={(value) => updateFormData({
                      commune_of_birth_code: value.commune_of_birth_code,
                      province_of_birth_code: value.province_of_birth_code,
                      born_abroad: value.born_abroad,
                      country_of_birth_code: value.country_of_birth_code,
                    })}
                    valid={isValid('commune_of_birth_code')}
                    error={errors.commune_of_birth_code?.message}
                    valid_country_of_birth_code={isValid(
                      'country_of_birth_code',
                    )}
                    error_country_of_birth_code={
                      errors.country_of_birth_code?.message
                    }
                  />
                </div>
              </div>
              <FormInputText
                registration={register('email')}
                label="E-mail"
                placeholder="nome@esempio.it"
                error={errors.email?.message}
                valid={isValid('email')}
              />
              <FormInputText
                registration={register('phone')}
                label="Phone"
                placeholder="Es. 3401234567"
                paragraph="Numeri di rete fissa non accettati dalle aziende"
                error={errors.phone?.message}
                valid={isValid('phone')}
              />

              {showManualAdress ? (
                <FormInputAddressCompleteManual
                  municipalities={municipalities}
                  formData={formDataFlow}
                  isValid={isValid}
                  errors={errors}
                  register={register}
                  updateFormData={updateFormData}
                />
              ) : (
                <FormInputAddressComplete
                  municipalities={municipalities}
                  formData={formDataFlow}
                  isValid={isValid}
                  errors={errors}
                  register={register}
                  updateFormData={updateFormData}
                  addressId="autocomplete-address-contractor"
                  setShow={setShowManualAdress}
                />
              )}

              <FormInputDate
                label="Data Decorrenza"
                registration={register('policy_effective_date')}
                minDate={moment().format('YYYY-MM-DD')}
                error={errors.policy_effective_date?.message}
                valid={isValid('policy_effective_date')}
              />
            </FormLayout>
          </div>
          <FormLayout>
            <div className="terms">
              <SurfaceCardHeading>Informativa Privacy e IVASS</SurfaceCardHeading>

              <div className="text-decoration-underline mt-1 mb-2">
                <a
                  onClick={() => greeniaPrivacyPolicy()}
                  style={{ cursor: 'pointer' }}
                >
                  <small>Greenia Privacy Policy</small>
                </a>
              </div>
              {dataPrivacy.map((privacy, i) => (
                <InputPrivacy
                  required={privacy.required}
                  label={privacy.content}
                  name={privacy.type}
                  id={privacy.type}
                  key={i}
                  checked={formDataFlow?.privacies?.includes(privacy.id)}
                  hasError={showPrivacyErrors && privacy.required === 1 && !formDataFlow?.privacies?.includes(privacy.id)}
                  onChange={(value) => privacyItemChangeHandler(value, privacy)}
                />
              ))}
            </div>
          </FormLayout>

        </PubblicoImpiegoFormStyles>
      )}
      <FooterNav className="footer-buttons button-travel-step">
        <div className="d-flex justify-content-between">
          {currentIndex !== 1 && (
            <SecondaryButton
              type="button"
              className="btn btn-questionnaire back backtoStepOne backtoStepTwo backtoStepThree"
              onClick={() => setCurrentIndex(1)}
            >
              Indietro
            </SecondaryButton>
          )}
          {currentIndex === 1 ? (
            <PrimaryButton
              type="button"
              onClick={() => nextButton()}
              className="btn btn-questionnaire"
            >
              Continua
            </PrimaryButton>
          ) : (
            <PrimaryButton type="button" onClick={() => submitForm(formDataFlow)} className="btn btn-questionnaire">
              Vai a preventivo
            </PrimaryButton>
          )}
        </div>
      </FooterNav>
    </PubblicoImpiegoFormFlowStyles>
  );
}

export default PubblicoImpiegoFormFlow;
